export const canEditCookbook = (user) =>
  Boolean(user && user.roles && (user.roles.includes("ROLE_CHIEF") || user.roles.includes("ROLE_ADMIN")));

// AI chat apps often wrap the whole answer in ```markdown ... ```; unwrap it.
export const normalizeMarkdown = (text) => {
  const markdown = (text || "").replace(/\r\n/g, "\n").trim();
  const fenced = markdown.match(/^```(?:markdown|md)?[ \t]*\n([\s\S]*)\n```$/i);
  return fenced ? fenced[1].trim() : markdown;
};

// Text of the first "# Heading", used as the default entry title.
export const extractTitle = (markdown) => {
  const match = normalizeMarkdown(markdown).match(/^#[ \t]+(.+?)[ \t#]*$/m);
  return match ? match[1].replace(/[*_`]/g, "").trim() : "";
};

// The note's own "# Title" line, which the entry page already shows as its header.
export const stripLeadingTitle = (markdown) => normalizeMarkdown(markdown).replace(/^#[ \t]+.*(\n|$)/, "").trim();

export const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "") || "topic";

export const formatDate = (value) =>
  new Date(value).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" });

export const PROMPT_LANGUAGES = {
  en: "English",
  el: "Ελληνικά",
};

// The prompt to paste into Claude / ChatGPT; it fixes the note's structure so every
// cookbook entry reads the same way.
export const buildCookbookPrompt = ({ topic, specialty, language }) => {
  const name = topic.trim() || "[TOPIC]";
  const audience = specialty.trim() ? `a ${specialty.trim()} resident` : "a resident doctor";
  const languageRule =
    language === "el"
      ? "Write in Greek (including the section headings), keeping drug names, classification names and standard abbreviations in their usual form."
      : "Write in English.";

  return `You are writing a high-yield reference note for my residency "cookbook": a personal collection of topic notes I use to prepare for ward rounds, on-call shifts, the operating room and exams.

Topic: ${name}
Audience: ${audience} who must be ready to recognise and manage this on the ward, in the emergency department and on call.
${languageRule}

Cover everything I need to know. Be comprehensive but scannable: short bullets, tables for comparisons and classifications, and **bold** for anything that changes management (red flags, cut-offs, doses).

Use exactly this structure (add ### subsections where useful; leave out a section only if it truly does not apply):

# ${name}

> Bottom line in 2-3 sentences: what it is, who gets it, how it presents and the single most important management point.

## High-yield summary
5-10 bullets I must not forget.

## Definition & classification
Relevant classification, grading and scoring systems as tables (grade | criteria | management implication).

## Epidemiology & risk factors

## Pathophysiology

## Clinical presentation
History, examination findings, atypical presentations and red flags.

## Differential diagnosis
Table: diagnosis | distinguishing features | key test.

## Investigations
Labs, imaging (first-line, when to escalate, key findings to look for) and decision rules.

## Management
Step by step, stratified by severity and setting: initial resuscitation and emergency department management; medical/conservative treatment (drug names, typical adult doses, duration); interventional options; surgical management (indications, timing, operation options, key operative principles).

## Complications
Of the disease and of its treatment, and how to recognise and manage each.

## Follow-up & prognosis

## On call: practical checklist
When I am called about this patient: initial assessment, admission orders, what to tell my senior, and when to escalate urgently.

## Pearls & pitfalls

## Questions I might be asked
8-12 likely questions from seniors on rounds or in exams, each followed by a concise answer.

## Guidelines & key references
The major current guidelines (society + year) and landmark trials this note is based on.

Rules:
- Base the note on current international guidelines and standard textbooks; where guidelines disagree or practice varies, say so briefly.
- Give typical adult doses and note where local protocols may differ.
- Do not invent references, trials or numbers. If you are unsure of a detail, say so instead of guessing.
- Use GitHub-flavoured Markdown only (headings, lists, tables, bold, blockquotes); no HTML.
- Output only the Markdown document, starting with the "# ${name}" line: no introduction, no closing remarks, and do not wrap it in a code block.`;
};
